import { always } from '@kofno/piper';
import { array, boolean, Decoder, field, maybe, number, string, succeed } from 'jsonous';
import { err, ok } from 'resulty';
import { linksDecoder, resourceDecoder } from '../../../Resource/Decoders';
import {
  CollectionSummary,
  CommunicationEntry,
  CommunicationGroup,
  EffectiveTier,
  InheritedStatus,
  InheritedTemplatePayload,
  InheritedTemplateResource,
  Override,
  OverridesIndexPayload,
  OverridesIndexResource,
  PreviewPayload,
  PreviewResource,
  SampleParticipant,
} from './Types';

const effectiveTierDecoder: Decoder<EffectiveTier> = string.andThen((value) => {
  switch (value) {
    case 'collection':
    case 'organization':
    case 'default':
      return new Decoder<EffectiveTier>(always(ok(value)));
    default:
      return new Decoder<EffectiveTier>(
        always(err(`Expected an effective tier, instead found ${value}`)),
      );
  }
});

const overrideDecoder: Decoder<Override> = succeed({})
  .assign('id', field('id', number))
  .assign('subject', field('subject', string))
  .assign('content', field('content', maybe(string)))
  .assign('ctaLabel', field('cta_label', maybe(string)))
  .assign('ctaUrl', field('cta_url', maybe(string)))
  .assign('links', field('links', linksDecoder));

// Unlike the tier, an unrecognised group is not a contract violation -- it
// just means the server mapped a trigger this build has no heading for yet.
// That should leave the communication unlabelled, not fail the whole page.
const toGroup = (value: string): CommunicationGroup => {
  switch (value) {
    case 'program_invitations':
      return 'program_invitations';
    case 'coaching_invitations':
      return 'coaching_invitations';
    case 'enrollment_reminders':
      return 'enrollment_reminders';
    case 'getting_started':
      return 'getting_started';
    case 'group_coaching':
      return 'group_coaching';
    case 'during_the_program':
      return 'during_the_program';
    case 'completion':
      return 'completion';
    case 'account':
      return 'account';
    default:
      return 'other';
  }
};

const communicationGroupDecoder: Decoder<CommunicationGroup> = string.andThen((value) =>
  succeed(toGroup(value)),
);

const communicationEntryDecoder: Decoder<CommunicationEntry> = succeed({})
  .assign('communicationId', field('communication_id', number))
  .assign('name', field('name', string))
  .assign('publicName', field('public_name', maybe(string)))
  .assign('trigger', field('trigger', maybe(string)))
  .assign('group', field('group', communicationGroupDecoder))
  .assign('effectiveTier', field('effective_tier', effectiveTierDecoder))
  .assign('disabled', field('disabled', boolean))
  .assign('disableable', field('disableable', boolean))
  .assign('disableRestriction', field('disable_restriction', maybe(string)))
  .assign('defaultSubject', field('default_subject', string))
  .assign('organizationSubject', field('organization_subject', maybe(string)))
  .assign('override', field('override', maybe(overrideDecoder)))
  .assign('links', field('links', linksDecoder));

const inheritedStatusDecoder: Decoder<InheritedStatus> = string.andThen((value) => {
  switch (value) {
    case 'ok':
    case 'missing':
    case 'unavailable':
    case 'not_extractable':
      return new Decoder<InheritedStatus>(always(ok(value)));
    default:
      return new Decoder<InheritedStatus>(
        always(err(`Expected an inherited status, instead found ${value}`)),
      );
  }
});

const inheritedTemplatePayloadDecoder: Decoder<InheritedTemplatePayload> = succeed({})
  .assign('communicationId', field('communication_id', number))
  .assign('tier', field('tier', maybe(string)))
  .assign('status', field('status', inheritedStatusDecoder))
  .assign('subject', field('subject', maybe(string)))
  .assign('content', field('content', maybe(string)))
  .assign('ctaLabel', field('cta_label', maybe(string)))
  .assign('ctaUrl', field('cta_url', maybe(string)));

export const inheritedTemplateResourceDecoder: Decoder<InheritedTemplateResource> = resourceDecoder(
  inheritedTemplatePayloadDecoder,
);

const collectionSummaryDecoder: Decoder<CollectionSummary> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string));

const overridesIndexPayloadDecoder: Decoder<OverridesIndexPayload> = succeed({})
  .assign('collection', field('collection', collectionSummaryDecoder))
  .assign('communications', field('communications', array(communicationEntryDecoder)));

export const overridesIndexResourceDecoder: Decoder<OverridesIndexResource> = resourceDecoder(
  overridesIndexPayloadDecoder,
);

const sampleParticipantDecoder: Decoder<SampleParticipant> = succeed({})
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string))
  .assign('email', field('email', string));

const previewPayloadDecoder: Decoder<PreviewPayload> = succeed({})
  .assign('subject', field('subject', string))
  .assign('html', field('html', string))
  .assign('unreplacedTokens', field('unreplaced_tokens', array(string)))
  .assign('conditionalTokens', field('conditional_tokens', array(string)))
  .assign('sampleParticipant', field('sample_participant', sampleParticipantDecoder));

export const previewResourceDecoder: Decoder<PreviewResource> =
  resourceDecoder(previewPayloadDecoder);
